import React, { useEffect, useState } from 'react';
import HomeLayout from '../../asset/layout/HomeLayout';
import { ProfileCard } from '../../components/Cards/ProfileCard';
import {
  Box,
  Card,
  Container,
  Grid,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { motion } from 'framer-motion';
import { MasterCard } from '../../components/Cards/MasterCard';
import Chart from '../../components/Chart/Chart';
import ListCard from '../../components/Cards/ListCard';
import { useDispatch } from 'react-redux';
import {
  getDashboardRecentTransactions,
  getDashboardRecentUsers,
  getDashboardStats,
} from '../../actions/dashboardAuth';
import {
  getFlipkartBalance,
  getWoohooWalletBalance,
  getGyftrWalletBalance,
  getHubbleBalance,
} from '../../actions/brandsAuth';
import { getTransactionAnalytics } from '../../actions/transactionsAuth';
import { useNavigate } from 'react-router-dom';
import TransactionAnalytics from '../../components/TransactionAnalytics';
import { RevenueAnalyticsPanel } from '../analytics/RevenueAnalytics';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

const iconData = [
  { src: '/Ticket.svg', alt: 'Vouchers', name: 'Vouchers' },
  { src: '/flipkart.svg', alt: 'Flipkart Balance', name: 'Flipkart' },
  { src: '/flipkart.svg', alt: 'Flipkart Discount', name: 'FlipkartDiscount' },
  { src: '/Card.svg', alt: 'Woohoo Balance', name: 'Woohoo' },
  { src: '/Card.svg', alt: 'Gyftr Balance', name: 'Gyftr' },
  { src: '/Card.svg', alt: 'Hubble Balance', name: 'Hubble' },
  { src: '/Users.svg', alt: 'Total users', name: 'Total Users' },
  { src: '/Users.svg', alt: 'Active users', name: 'Active Users' },
  { src: '/Users.svg', alt: 'Inactive users', name: 'Inactive Users' },
  { src: '/Users.svg', alt: 'New users', name: 'New Users' },
  { src: '/Card.svg', alt: 'Total Amount', name: 'Total Amount' },
  { src: '/arrowgreen.svg', alt: 'Success', name: 'Succes Transactions' },
  { src: '/arrowRed.svg', alt: 'Failed', name: 'Failed Transactions' },
  { src: '/Transactions.svg', alt: 'Pending', name: 'Pending Transactions' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #11409B 0%, #2563eb 100%)',
        color: '#fff',
        boxShadow: '0 4px 16px rgba(17, 64, 155, 0.3)',
      }}
    >
      <Icon sx={{ fontSize: 22 }} />
    </Box>
    <Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: '#0f172a',
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Stack>
);

export const DashBoard = () => {
  const defaultStats = {
    vouchers: 0,
    flipkart: 0,
    flipkartAmount: 0,
    flipkartDiscount: 0,
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    newUsers: 0,
    totalAmount: 0,
    successTransactions: 0,
    failedTransactions: 0,
    pendingTransactions: 0,
    gyftrBalance: 0,
    hubbleBalance: 0,
  };

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(defaultStats);
  const [vouchers, setVouchers] = useState(0);
  const [flipkartBalance, setFlipkartBalance] = useState(null);
  const [flipkartDiscount, setFlipkartDiscount] = useState(null);
  const [woohooBalance, setWoohooBalance] = useState(null);
  const [gyftrBalance, setGyftrBalance] = useState(null);
  const [hubbleBalance, setHubbleBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const adminRole = localStorage.getItem('SiriAdminUserRole') || 'Admin';

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    dispatch(
      getDashboardStats(
        (data) => {
          const dashboardStats = data?.data?.data ?? data?.data ?? defaultStats;
          setStats({ ...defaultStats, ...dashboardStats });
          setVouchers(Number(dashboardStats?.vouchers) || 0);
        },
        (err) => console.log(err)
      )
    );

    dispatch(
      getDashboardRecentTransactions(
        (data) => {
          const list = data?.data?.data ?? data?.data ?? [];
          setTransactions(Array.isArray(list) ? list : []);
        },
        (err) => console.log(err)
      )
    );

    dispatch(
      getDashboardRecentUsers(
        (data) => {
          const recentUsers = data?.data?.data ?? data?.data ?? [];
          setUsers(Array.isArray(recentUsers) ? recentUsers : []);
        },
        (err) => console.log(err)
      )
    );

    dispatch(
      getFlipkartBalance(
        ({ balance, discountPercentage }) => {
          setFlipkartBalance(balance);
          setFlipkartDiscount(discountPercentage);
        },
        (err) => console.log(err)
      )
    );

    dispatch(
      getWoohooWalletBalance(
        ({ balance }) => {
          setWoohooBalance(balance);
        },
        (err) => console.log(err)
      )
    );

    dispatch(
      getGyftrWalletBalance(
        ({ balance }) => {
          setGyftrBalance(balance);
        },
        (err) => console.log(err)
      )
    );

    dispatch(
      getHubbleBalance(
        ({ balance }) => {
          setHubbleBalance(balance);
        },
        (err) => console.log(err)
      )
    );

    dispatch(
      getTransactionAnalytics(
        {},
        (data) => {
          const analyticsData = data?.data?.data ?? data?.data ?? data;
          setAnalytics(analyticsData);
          setAnalyticsLoading(false);
        },
        (err) => {
          console.log('Error fetching transaction analytics:', err);
          setAnalytics(null);
          setAnalyticsLoading(false);
        }
      )
    );
  }, [dispatch]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
      }}
    >
      <HomeLayout>
        <Container maxWidth="xl" sx={{ pb: 6 }}>
          {/* Welcome header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <Box
              sx={{
                mt: 3,
                mb: 4,
                p: { xs: 3, md: 4 },
                borderRadius: '24px',
                background:
                  'linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(17, 64, 155, 0.35)',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -60,
                  right: -40,
                  width: 220,
                  height: 220,
                  borderRadius: '50%',
                  bgcolor: alpha('#fff', 0.06),
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -80,
                  left: '30%',
                  width: 280,
                  height: 280,
                  borderRadius: '50%',
                  bgcolor: alpha('#38bdf8', 0.12),
                }}
              />
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ position: 'relative', zIndex: 1 }}
              >
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <DashboardCustomizeOutlinedIcon sx={{ fontSize: 22 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: '0.1em',
                        opacity: 0.85,
                      }}
                    >
                      DASHBOARD OVERVIEW
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: '-0.03em',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    Welcome back
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mt: 1, opacity: 0.82, maxWidth: 480 }}
                  >
                    Monitor vouchers, users, and transactions at a glance.
                    You&apos;re signed in as{' '}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {adminRole}
                    </Box>
                    .
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: '16px',
                    bgcolor: alpha('#fff', 0.12),
                    border: `1px solid ${alpha('#fff', 0.2)}`,
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    Live metrics
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {iconData.length} KPIs tracked
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </motion.div>

          {/* Stat cards */}
          <SectionHeader
            icon={ShowChartOutlinedIcon}
            title="Key metrics"
            subtitle="Real-time stats from your platform"
          />
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            {iconData.map((data, index) => (
              <Grid item key={data.name} xs={12} sm={6} md={4} lg={3}>
                <motion.div
                  custom={index}
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  whileHover={{ y: -2 }}
                  style={{ height: '100%' }}
                >
                  <MasterCard
                    {...data}
                    users={users}
                    vouchers={vouchers}
                    totalUsers={stats?.totalUsers}
                    totalAmount={stats?.totalAmount}
                    flipkartAmount={
                      flipkartBalance ??
                      stats?.flipkartAmount ??
                      stats?.flipkart ??
                      0
                    }
                    flipkartDiscount={
                      flipkartDiscount ??
                      stats?.flipkartDiscount ??
                      0
                    }
                    woohooBalance={woohooBalance ?? stats?.woohooBalance ?? 0}
                    gyftrBalance={gyftrBalance ?? stats?.gyftrBalance ?? 0}
                    hubbleBalance={hubbleBalance ?? stats?.hubbleBalance ?? 0}
                    activeUsers={stats?.activeUsers}
                    inactiveUsers={stats?.inactiveUsers}
                    newUsers={stats?.newUsers}
                    successTransactions={stats?.successTransactions}
                    failedTransactions={stats?.failedTransactions}
                    pendingTransactions={stats?.pendingTransactions}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={12}
          >
            <TransactionAnalytics analytics={analytics} loading={analyticsLoading} />
          </motion.div>

          {/* Chart + recent transactions */}
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <SectionHeader
                icon={ShowChartOutlinedIcon}
                title="Weekly activity"
                subtitle="Success, pending & failed orders"
              />
              <PanelCard sx={{ height: { xs: 360, md: 420 } }}>
                <Box sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
                  <Chart />
                </Box>
              </PanelCard>
            </Grid>

            {/* <Grid item xs={12} lg={4}>
              <SectionHeader
                icon={ReceiptLongOutlinedIcon}
                title="Recent transactions"
                subtitle="Latest payment activity"
              />
              <ListCard transactions={transactions} />
            </Grid> */}
          </Grid>

          {/* Recent users */}
          <Box sx={{ mt: 5 }}>
            <SectionHeader
              icon={GroupOutlinedIcon}
              title="Recent users"
              subtitle={
                users.length
                  ? `${users.length} newest registrations`
                  : 'No users to display yet'
              }
            />
            <Grid container spacing={2.5}>
              {users.length === 0 ? (
                <Grid item xs={12}>
                  <PanelCard>
                    <Typography
                      sx={{ p: 4, textAlign: 'center', color: '#64748b' }}
                    >
                      New users will appear here once available.
                    </Typography>
                  </PanelCard>
                </Grid>
              ) : (
                users.map((data, index) => (
                  <Grid item key={data.id || data._id || index} xs={6} sm={4} md={3} lg={2.4}>
                    <motion.div
                      custom={index}
                      initial="hidden"
                      animate="visible"
                      variants={fadeUp}
                    >
                      <ProfileCard
                        username={data.username || data.name}
                        phone={data.phone}
                        email={data.email}
                        image={data}
                        dis={data.dis}
                        showMessageButton={false}
                        premium
                      />
                    </motion.div>
                  </Grid>
                ))
              )}
            </Grid>
          </Box>

          {/* Revenue analytics at bottom of dashboard */}
          <Box sx={{ mt: 5 }}>
            <SectionHeader
              icon={InsightsOutlinedIcon}
              title="Revenue analytics"
              subtitle="Sales, vendor, gateway, refunds, rewards, profit & reconciliation"
            />
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={20}
            >
              <RevenueAnalyticsPanel embedded />
            </motion.div>
          </Box>
        </Container>
      </HomeLayout>
    </Box>
  );
};
